import { useCallback, useState } from "react";
import { toast } from "sonner";
import { workspaceKnowledgeApi } from "@/api/workspaceKnowledgeApi";
import { useCollectionDetail } from "@/pages/settings/knowledge/hooks/useCollectionDetail";
import { useKnowledgeCollections } from "@/pages/settings/knowledge/hooks/useKnowledgeCollections";

function Modal({ title, onClose, children }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kb-modal-title"
      onClick={onClose}
    >
      <div
        className="ui-card-static max-h-[90vh] w-full max-w-lg overflow-y-auto !p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          <h4 id="kb-modal-title" className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {title}
          </h4>
          <button type="button" className="ui-btn-ghost !p-2" onClick={onClose} aria-label="Close">
            <i className="ri-close-line text-lg" aria-hidden />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function KnowledgeRagPanel({ ragEnabled, topK, maxContextChars, onChange, onSave, saving }) {
  return (
    <div className="space-y-6">
      <p className="text-xs text-slate-500 dark:text-slate-400">
        When enabled, chat-service searches workspace knowledge collections during widget replies (requires{" "}
        <span className="font-mono text-[11px]">KB_MANAGER_BASE_URL</span> on chat-service).
      </p>
      <div className="flex items-center justify-between gap-4 border-b border-slate-100 pb-5 dark:border-slate-700">
        <div>
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">RAG retrieval</p>
          <p className="mt-1 text-xs text-slate-500">Inject indexed excerpts into the model context.</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={ragEnabled}
          onClick={() => onChange({ ragEnabled: !ragEnabled })}
          className={`inline-flex h-7 w-12 shrink-0 items-center rounded-full p-1 ${
            ragEnabled ? "ui-toggle-track-on" : "ui-toggle-track-off"
          }`}
        >
          <span
            className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
              ragEnabled ? "translate-x-5" : "translate-x-0"
            }`}
          />
        </button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1.5 block text-sm font-semibold text-slate-800 dark:text-slate-100">Top K</span>
          <input
            type="number"
            min={1}
            max={50}
            className="ui-field"
            value={topK}
            onChange={(e) => onChange({ topK: parseInt(e.target.value, 10) || 5 })}
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-semibold text-slate-800 dark:text-slate-100">
            Max context characters
          </span>
          <input
            type="number"
            min={500}
            max={32000}
            step={100}
            className="ui-field"
            value={maxContextChars}
            onChange={(e) => onChange({ maxContextChars: parseInt(e.target.value, 10) || 4000 })}
          />
        </label>
      </div>
      <button type="button" className="ui-btn-primary" disabled={saving} onClick={() => void onSave()}>
        {saving ? "Saving…" : "Save RAG settings"}
      </button>
    </div>
  );
}

export default function KnowledgeBaseSection({
  ragEnabled,
  topK,
  maxContextChars,
  onRagChange,
  onRagSave,
  savingRag,
}) {
  const {
    collections,
    loading: listLoading,
    error: listError,
    isEmpty: listEmpty,
    refresh: refreshList,
  } =
    useKnowledgeCollections();
  const [selectedId, setSelectedId] = useState(null);
  const effectiveId =
    selectedId || (collections[0]?.id != null ? String(collections[0].id) : null);
  const { detail, loading: detailLoading, error: detailError, refresh: refreshDetail } =
    useCollectionDetail(effectiveId);

  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showUrls, setShowUrls] = useState(false);

  const [createName, setCreateName] = useState("");
  const [createDesc, setCreateDesc] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [urlText, setUrlText] = useState("");
  const [urlTitle, setUrlTitle] = useState("");

  const selectCollection = (id) => {
    setSelectedId(String(id));
  };

  const afterMutation = useCallback(async () => {
    await refreshList();
    if (effectiveId) await refreshDetail();
  }, [refreshList, refreshDetail, effectiveId]);

  const handleCreate = async () => {
    const name = createName.trim();
    if (!name) {
      toast.error("Collection name is required.");
      return;
    }
    setBusy(true);
    try {
      const res = await workspaceKnowledgeApi.createCollection({
        display_name: name,
        description: createDesc.trim(),
      });
      const id = res?.data?.id;
      setShowCreate(false);
      setCreateName("");
      setCreateDesc("");
      toast.success("Collection created.");
      await refreshList();
      if (id) setSelectedId(String(id));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Create failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleUpload = async () => {
    if (!effectiveId || !uploadFile) {
      toast.error("Select a collection and file.");
      return;
    }
    setBusy(true);
    try {
      await workspaceKnowledgeApi.uploadDocument(effectiveId, uploadFile);
      setShowUpload(false);
      setUploadFile(null);
      toast.success("Document indexed.");
      await afterMutation();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleUrls = async () => {
    if (!effectiveId) {
      toast.error("Select a collection first.");
      return;
    }
    const lines = urlText
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    if (!lines.length) {
      toast.error("Enter at least one URL.");
      return;
    }
    setBusy(true);
    let ok = 0;
    let fail = 0;
    let lastError = null;
    try {
      for (const url of lines) {
        try {
          await workspaceKnowledgeApi.ingestUrl(effectiveId, { url, title: urlTitle.trim() });
          ok += 1;
        } catch (e) {
          fail += 1;
          lastError = e instanceof Error ? e.message : String(e);
        }
      }
      setShowUrls(false);
      setUrlText("");
      setUrlTitle("");
      if (fail && lastError) {
        toast.error(`URL ingest: ${ok} succeeded, ${fail} failed. ${lastError}`);
      } else {
        toast.message(`URL ingest: ${ok} succeeded${fail ? `, ${fail} failed` : ""}.`);
      }
      await afterMutation();
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteSource = async (source) => {
    if (!effectiveId || !source?.id) return;
    const label = source.title || source.original_filename || source.source_url || "this source";
    if (!window.confirm(`Remove "${label}" from the index? This rebuilds the Qdrant collection without its vectors.`)) {
      return;
    }
    setBusy(true);
    try {
      await workspaceKnowledgeApi.deleteSource(effectiveId, String(source.id));
      toast.success("Source removed.");
      await afterMutation();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not delete source.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!effectiveId) return;
    if (!window.confirm("Delete this collection and all indexed sources?")) return;
    setBusy(true);
    try {
      await workspaceKnowledgeApi.deleteCollection(effectiveId);
      toast.success("Collection deleted.");
      setSelectedId(null);
      await refreshList();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Delete failed.");
    } finally {
      setBusy(false);
    }
  };

  const rev = detail?.current_revision;
  const sources = Array.isArray(detail?.sources) ? detail.sources : [];

  return (
    <div className="space-y-8">
      <div className="ui-card-static !p-6 sm:!p-8">
        <h4 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">RAG settings</h4>
        <KnowledgeRagPanel
          ragEnabled={ragEnabled}
          topK={topK}
          maxContextChars={maxContextChars}
          onChange={onRagChange}
          onSave={onRagSave}
          saving={savingRag}
        />
      </div>

      <div className="ui-card-static !p-6 sm:!p-8">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Knowledge collections</h4>
            <p className="mt-1 text-xs text-slate-500">
              Each upload or URL adds one indexed entry. Indexing runs automatically (copy-forward revisions in
              Qdrant).
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="ui-btn-primary" disabled={busy} onClick={() => setShowCreate(true)}>
              <i className="ri-folder-add-line mr-1" aria-hidden />
              New collection
            </button>
            <button
              type="button"
              className="ui-btn-secondary"
              disabled={busy || !effectiveId}
              onClick={() => setShowUpload(true)}
            >
              <i className="ri-upload-line mr-1" aria-hidden />
              Upload document
            </button>
            <button
              type="button"
              className="ui-btn-secondary"
              disabled={busy || !effectiveId}
              onClick={() => setShowUrls(true)}
            >
              <i className="ri-links-line mr-1" aria-hidden />
              Upload URLs
            </button>
          </div>
        </div>

        {listError && !listLoading && (
          <p className="mb-4 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-800 ring-1 ring-rose-200">{listError}</p>
        )}

        <div className="grid gap-6 lg:grid-cols-[minmax(200px,280px)_1fr]">
          <div className="space-y-1 border-r-0 lg:border-r lg:border-slate-100 lg:pr-4 dark:lg:border-slate-700">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-slate-500">Collections</p>
            {listLoading && <p className="text-xs text-slate-500">Loading…</p>}
            {listEmpty && (
              <p className="text-xs text-slate-500">No collections yet. Create one to start.</p>
            )}
            {collections.map((c) => {
              const id = String(c.id);
              const active = id === effectiveId;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => selectCollection(id)}
                  className={`w-full rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                    active
                      ? "bg-teal-50 font-semibold text-teal-900 ring-1 ring-teal-200 dark:bg-teal-950/40 dark:text-teal-100"
                      : "text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800"
                  }`}
                >
                  {c.display_name}
                </button>
              );
            })}
          </div>

          <div>
            {!effectiveId && listEmpty && (
              <p className="text-sm text-slate-500">Your knowledge base is empty. Create a collection to add documents or URLs.</p>
            )}
            {!effectiveId && !listLoading && !listError && collections.length > 0 && (
              <p className="text-sm text-slate-500">Select or create a collection.</p>
            )}
            {effectiveId && detailLoading && <p className="text-sm text-slate-500">Loading collection…</p>}
            {detailError && (
              <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-800">{detailError}</p>
            )}
            {effectiveId && detail && !detailLoading && (
              <div className="space-y-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h5 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                      {detail.display_name}
                    </h5>
                    {detail.description && (
                      <p className="mt-1 text-sm text-slate-500">{detail.description}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    className="ui-btn-secondary text-rose-700 ring-rose-200 hover:bg-rose-50"
                    disabled={busy}
                    onClick={() => void handleDelete()}
                  >
                    Delete collection
                  </button>
                </div>

                {rev ? (
                  <div className="rounded-xl bg-slate-50 px-4 py-3 text-xs text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800/50 dark:text-slate-300 dark:ring-slate-600">
                    <p>
                      <span className="font-semibold">Indexed points:</span> {rev.point_count ?? 0}
                    </p>
                    <p className="mt-1">
                      <span className="font-semibold">Embedding model:</span> {rev.embedding_model}
                    </p>
                    <p className="mt-1 truncate font-mono text-[10px] opacity-80" title={rev.qdrant_collection_name}>
                      Qdrant: {rev.qdrant_collection_name}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    No sources indexed yet. Upload a document or add a URL.
                  </p>
                )}

                <div>
                  <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-slate-500">Sources</p>
                  {sources.length === 0 ? (
                    <p className="text-xs text-slate-500">No sources yet.</p>
                  ) : (
                    <ul className="divide-y divide-slate-100 rounded-lg ring-1 ring-slate-200 dark:divide-slate-700 dark:ring-slate-600">
                      {sources.map((s) => (
                        <li
                          key={String(s.id)}
                          className="flex items-start justify-between gap-2 px-3 py-2 text-sm"
                        >
                          <div className="min-w-0 flex-1">
                            <span className="font-medium text-slate-800 dark:text-slate-100">
                              {s.title || s.original_filename || s.source_url || "Source"}
                            </span>
                            <span className="ml-2 text-[10px] uppercase text-slate-400">{s.source_kind}</span>
                            {s.source_url && (
                              <p className="mt-0.5 truncate text-xs text-slate-500">{s.source_url}</p>
                            )}
                          </div>
                          <button
                            type="button"
                            className="ui-btn-ghost shrink-0 !px-2 !py-1 text-xs text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                            disabled={busy}
                            onClick={() => void handleDeleteSource(s)}
                            aria-label={`Delete source ${s.title || s.id}`}
                          >
                            <i className="ri-delete-bin-line" aria-hidden />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showCreate && (
        <Modal title="New collection" onClose={() => setShowCreate(false)}>
          <div className="space-y-4">
            <label className="block">
              <span className="mb-1 block text-sm font-semibold">Name</span>
              <input
                className="ui-field w-full"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="Product docs"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-semibold">Description (optional)</span>
              <textarea
                className="ui-field w-full resize-y"
                rows={3}
                value={createDesc}
                onChange={(e) => setCreateDesc(e.target.value)}
              />
            </label>
            <button type="button" className="ui-btn-primary w-full" disabled={busy} onClick={() => void handleCreate()}>
              {busy ? "Creating…" : "Create"}
            </button>
          </div>
        </Modal>
      )}

      {showUpload && (
        <Modal title="Upload document" onClose={() => setShowUpload(false)}>
          <div className="space-y-4">
            <p className="text-xs text-slate-500">
              PDF, Word (.docx, .doc), or text files. Max size enforced by kb-manager.
            </p>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.txt,.md,.csv,text/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="ui-field w-full"
              onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
            />
            <button type="button" className="ui-btn-primary w-full" disabled={busy || !uploadFile} onClick={() => void handleUpload()}>
              {busy ? "Uploading…" : "Upload and index"}
            </button>
          </div>
        </Modal>
      )}

      {showUrls && (
        <Modal title="Upload URLs" onClose={() => setShowUrls(false)}>
          <div className="space-y-4">
            <label className="block">
              <span className="mb-1 block text-sm font-semibold">URLs (one per line)</span>
              <textarea
                className="ui-field w-full resize-y font-mono text-sm"
                rows={6}
                value={urlText}
                onChange={(e) => setUrlText(e.target.value)}
                placeholder="https://example.com/help/article"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-semibold">Default title (optional)</span>
              <input className="ui-field w-full" value={urlTitle} onChange={(e) => setUrlTitle(e.target.value)} />
            </label>
            <button type="button" className="ui-btn-primary w-full" disabled={busy} onClick={() => void handleUrls()}>
              {busy ? "Processing…" : "Ingest URLs"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
